import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { APP_ENV, type AppEnv } from './constants/env.constants';
import { type UserJwtPayload } from './types';

interface SentryOptions {
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  sendDefaultPii?: boolean;
  enableLogs?: boolean;
  enableReplays?: boolean;
}

export const setCaseTag = (caseId: string | null) => {
  if (caseId) {
    Sentry.setTag('case_id', caseId);
  } else {
    Sentry.setTag('case_id', null);
  }
};

interface SentryInitConfig {
  fetchUser?: () => Promise<{ id?: string; email?: string } | null>;
}

export const initSentry = async (config?: SentryInitConfig) => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV as AppEnv;
  const sentryOptionsJson = import.meta.env.VITE_SENTRY_OPTIONS;

  if (!dsn) {
    console.warn('🚫 Sentry DSN not configured. Error tracking will be disabled.');
    return;
  }

  if (!environment) {
    console.warn('⚠️ Application environment not configured. Defaulting to "LOCAL".');
  }

  // Parse Sentry options from environment variable with defaults
  let sentryOptions: SentryOptions = {};
  if (sentryOptionsJson) {
    try {
      sentryOptions = JSON.parse(sentryOptionsJson);
    } catch (error) {
      console.warn('⚠️ Failed to parse VITE_SENTRY_OPTIONS JSON, using defaults:', error);
    }
  }

  // Apply defaults
  const tracesSampleRate = sentryOptions.tracesSampleRate ?? 0;
  const replaysSessionSampleRate = sentryOptions.replaysSessionSampleRate ?? 0;
  const replaysOnErrorSampleRate = sentryOptions.replaysOnErrorSampleRate ?? 0;
  const sendDefaultPii = sentryOptions.sendDefaultPii ?? true;
  const enableLogs = sentryOptions.enableLogs ?? true;
  const enableReplays = sentryOptions.enableReplays ?? false;

  const integrations = [
    Sentry.browserTracingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn'] }),
    Sentry.featureFlagsIntegration(),
  ];

  // Add replay integration based on configuration
  if (enableReplays) {
    integrations.push(Sentry.replayIntegration());
  }

  Sentry.init({
    dsn,
    environment: environment || APP_ENV.LOCAL,
    sendDefaultPii,
    _experiments: { enableLogs },
    integrations,
    // Configure replay settings if replays are enabled
    ...(enableReplays && {
      replaysSessionSampleRate,
      replaysOnErrorSampleRate,
    }),
    tracesSampler: (samplingContext) => {
      const { attributes, inheritOrSampleWith } = samplingContext;

      // Sample more transactions for users experiencing errors
      if (attributes?.hasRecentErrors === true && environment === APP_ENV.PROD) {
        return 0.8;
      }

      // Default sampling rate
      return inheritOrSampleWith(tracesSampleRate);
    },
  });

  // Log successful Sentry initialization
  console.log(`✅ Sentry initialized successfully for environment: ${environment || APP_ENV.LOCAL}`);

  // Send initialization message to Sentry
  Sentry.addBreadcrumb({
    message: 'Sentry initialization completed',
    level: 'info',
    data: {
      environment: environment || APP_ENV.LOCAL,
      enableReplays,
      tracesSampleRate,
      timestamp: new Date().toISOString(),
    },
  });

  try {
    if (config?.fetchUser) {
      const user = await config.fetchUser();
      if (user) {
        Sentry.setUser({
          id: user.id || '',
          email: user.email || '',
        });
        console.log(`👤 Sentry user context set for: ${user.email}`);
      }
    } else {
      const authSession = await fetchAuthSession();
      const token = authSession.tokens?.accessToken;

      if (token) {
        const payload = token.payload as unknown as UserJwtPayload;
        Sentry.setUser({
          id: payload.sub,
          email: payload.email,
        });
        console.log(`👤 Sentry user context set for: ${payload.email}`);
      }
    }

    // Set up feature flags if needed
    const flagsIntegration = Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>('FeatureFlags');
    if (flagsIntegration) {
      flagsIntegration.addFeatureFlag('test-flag', false);
    }
  } catch (error) {
    console.warn('⚠️ Could not set user information in Sentry:', error);
    Sentry.captureException(error);
  }
};
